import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { AppError } from '../../api/contract';
import { useRepository } from '../../data/RepositoryProvider';
import { MutationError, toDialogError } from '../partners/PartnerFormDialog';
import { agentRepositoryToken } from './repository';
import type { AgentDetail, AgentStatus } from './types';

export function AgentStatusDialog({
  agent,
  onClose,
  onSaved,
  open,
}: {
  readonly agent: AgentDetail;
  readonly onClose: () => void;
  readonly onSaved: (agent: AgentDetail) => void;
  readonly open: boolean;
}) {
  const repository = useRepository(agentRepositoryToken);
  const { t } = useTranslation();
  const submittingRef = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Pick<AppError, 'message' | 'requestId'> | null>(null);
  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
    watch,
  } = useForm<{ readonly status: AgentStatus; readonly reason: string }>({
    defaultValues: { status: agent.status, reason: '' },
  });
  const proposed = watch('status');
  const blockedByUsers = proposed !== 'ACTIVE' && agent.activeUserCount > 0;

  useEffect(() => {
    if (!open) return;
    reset({ status: agent.status, reason: '' });
    setError(null);
    submittingRef.current = false;
    setBusy(false);
  }, [agent.status, open, reset]);

  const submit = handleSubmit(async (values) => {
    if (submittingRef.current || blockedByUsers) return;
    submittingRef.current = true;
    setBusy(true);
    setError(null);
    try {
      const result = await repository.changeStatus(agent.id, {
        status: values.status,
        reason: values.reason.trim(),
      });
      onSaved(result.data);
      onClose();
    } catch (submitError) {
      setError(toDialogError(submitError, t('agents.errors.mutation')));
    } finally {
      submittingRef.current = false;
      setBusy(false);
    }
  });

  return (
    <Dialog
      aria-describedby="agent-status-impact"
      fullWidth
      onClose={() => !submittingRef.current && onClose()}
      open={open}
    >
      <DialogTitle>{t('agents.status.title')}</DialogTitle>
      <DialogContent>
        <Stack
          component="form"
          id="agent-status-form"
          noValidate
          onSubmit={submit}
          spacing={2}
          sx={{ pt: 1 }}
        >
          {error === null ? null : <MutationError error={error} />}
          <Alert severity={blockedByUsers ? 'error' : 'warning'}>
            <Typography id="agent-status-impact">
              {t('agents.status.impact', {
                activeUsers: agent.activeUserCount,
                code: agent.code,
                current: agent.status,
                next: proposed,
              })}
            </Typography>
            <Typography variant="body2">{t('agents.status.activeUserBlock')}</Typography>
            <Typography variant="body2">{t('agents.status.closedTerminal')}</Typography>
          </Alert>
          <TextField
            defaultValue={agent.status}
            label={t('agents.fields.proposedStatus')}
            select
            {...register('status')}
          >
            <MenuItem value="ACTIVE">ACTIVE</MenuItem>
            <MenuItem value="SUSPENDED">SUSPENDED</MenuItem>
            <MenuItem value="CLOSED">CLOSED</MenuItem>
          </TextField>
          <TextField
            error={errors.reason !== undefined}
            helperText={errors.reason?.message}
            label={t('agents.fields.reason')}
            multiline
            required
            {...register('reason', {
              required: t('agents.validation.reasonRequired'),
              validate: (value) => value.trim().length > 0 || t('agents.validation.reasonRequired'),
            })}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={onClose}>
          {t('actions.cancel')}
        </Button>
        <Button
          color="warning"
          disabled={blockedByUsers}
          form="agent-status-form"
          loading={busy}
          type="submit"
          variant="contained"
        >
          {t('actions.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
